import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {slotDayName, slotKey, slotTimeLabel} from "business/callSlots";
import Dialog from "components/redesign/Dialog";
import CtaButton from "components/redesign/CtaButton";
import IstNote from "components/redesign/IstNote";
import {Check, Info} from "lucide-react";
import {includes, map, without} from "lodash";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {color, font, radius, type} from "common/tokens";

const useStyles = makeStyles({
  description: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: '0 0 24px',
  },
  panel: {
    border: `1px solid ${color.CARD_BORDER}`,
    borderRadius: radius.PANEL,
    padding: 20,
  },
  istNote: {
    justifyContent: 'center',
    margin: '-8px 0 16px',
  },
  panelTitle: {
    ...type.meta,
    fontWeight: font.BOLD,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: color.ACCENT_TEXT,
    margin: '0 0 16px',
    textAlign: 'center',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  option: {
    ...type.body,
    appearance: 'none',
    background: color.SURFACE,
    border: `1px solid ${color.CARD_BORDER}`,
    borderRadius: 12,
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '14px 16px',
    textAlign: 'left',
    color: color.TEXT_MUTED,
    cursor: 'pointer',
    '&:hover': {
      background: color.SURFACE_MUTED,
    },
  },
  optionSelected: {
    color: color.TEXT,
    fontWeight: font.SEMIBOLD,
  },
  box: {
    width: 22,
    height: 22,
    flexShrink: 0,
    borderRadius: 6,
    border: `1px solid ${color.BORDER}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'transparent',
  },
  boxSelected: {
    background: color.SUCCESS,
    borderColor: color.SUCCESS,
    color: color.ON_ACCENT,
  },
  day: {
    flex: 1,
  },
  time: {
    fontWeight: font.BOLD,
    color: 'inherit',
  },
  hint: {
    ...type.meta,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    color: color.TEXT_MUTED,
    margin: '16px 0 0',
  },
  hintIcon: {
    color: color.PURPLE,
    flexShrink: 0,
    marginTop: 2,
  },
});

const RECOMMENDED_MINIMUM = 3;

// Every slot goes back with the status it was left in, so the backend stores
// exactly what was shown.
const ConfirmSlotsDialog = ({slots, onClose, onConfirm}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const toggle = key => setSelectedKeys(keys => includes(keys, key) ? without(keys, key) : [...keys, key]);

  const confirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm(map(slots, slot => ({
        weekday: slot.weekday,
        hour: slot.hour,
        minute: slot.minute || 0,
        status: includes(selectedKeys, slotKey(slot)) ? 'CONFIRMED' : 'OFFERED',
      })));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('confirmSlotsDialogTitle')}
      prominentTitle
      maxWidth={560}
      footer={(
        <CtaButton
          fullWidth
          size={'large'}
          label={t('confirmSlots')}
          disabled={selectedKeys.length === 0}
          loading={submitting}
          onClick={confirm}/>
      )}>
      <p className={classes.description}>{t('confirmSlotsDescription')}</p>

      <div className={classes.panel}>
        <p className={classes.panelTitle}>{t('yourCommonSlots')}</p>
        <IstNote className={classes.istNote}/>
        <div className={classes.list}>
          {map(slots, slot => {
            const key = slotKey(slot);
            const isSelected = includes(selectedKeys, key);
            return (
              <button
                type={'button'}
                key={key}
                role={'checkbox'}
                aria-checked={isSelected}
                className={classNames(classes.option, isSelected && classes.optionSelected)}
                onClick={() => toggle(key)}>
                <span className={classNames(classes.box, isSelected && classes.boxSelected)}>
                  <Check size={15} strokeWidth={3}/>
                </span>
                <span className={classes.day}>{slotDayName(slot.weekday)}</span>
                <span className={classes.time}>{slotTimeLabel(slot.hour, slot.minute)}</span>
              </button>
            );
          })}
        </div>
        {slots.length >= RECOMMENDED_MINIMUM && (
          <p className={classes.hint}>
            <Info size={15} strokeWidth={2.25} className={classes.hintIcon}/>
            {t('confirmSlotsRecommendation')}
          </p>
        )}
      </div>
    </Dialog>
  );
};

export default ConfirmSlotsDialog;
